import {
  PROTOCOL_VERSION,
  ServerMessageSchema,
  type ClientMessage,
  type ReliableClientMessage,
  type RoomClosedReason,
  type RoomEvent,
  type ServerMessage,
} from '@bm/shared';
import { useAppStore } from './store.js';

const TOKEN_STORAGE_KEY = 'bm.sessionToken';
const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 8000;
/** Nach so vielen erfolglosen Versuchen wird seltener probiert — dem Akku zuliebe. */
const RECONNECT_PATIENT_AFTER = 6;
const RECONNECT_PATIENT_MAX_MS = 30_000;
const PING_INTERVAL_MS = 15_000;
/** Ohne Antwort auf zwei Pings gilt die Leitung als tot, nicht als langsam. */
const PONG_TIMEOUT_MS = PING_INTERVAL_MS * 2 + 5_000;
/** Ein Verbindungsaufbau, der so lange hängt, wird abgebrochen statt abgewartet. */
const CONNECT_TIMEOUT_MS = 10_000;
/** Gepufferte Aktionen: Obergrenzen, damit die Outbox nicht zum Archiv wird. */
const OUTBOX_LIMIT = 20;
const OUTBOX_MAX_AGE_MS = 60_000;
const RELOAD_DELAY_MS = 4_000;

function wsUrl(): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/ws`;
}

/**
 * Wo der Session-Token liegt — und warum das vom Build abhängt.
 *
 * `localStorage` teilen sich alle Tabs derselben Herkunft. Ein zweiter Tab
 * meldet sich damit als dieselbe Session an, und der Server lässt pro Session
 * nur eine Verbindung zu: Der erste Tab fliegt raus (`session:takeover`). Beim
 * Entwickeln ist genau das im Weg, weil man mehrere Spieler an einem Rechner
 * braucht — also nimmt der Dev-Build `sessionStorage`, den jeder Tab für sich
 * hat. Wichtig dabei: einen *frischen* Tab öffnen. Beim Duplizieren eines Tabs
 * und bei Strg+Klick kopieren Browser den `sessionStorage` mit, und der
 * Takeover ist zurück.
 *
 * Der Preis gilt nur in der Entwicklung: Ein geschlossener Tab verliert seinen
 * Platz, weil `sessionStorage` mit ihm stirbt. Zurück führt der reguläre
 * Wiedereinstieg (`rejoin:*`) — fürs Testen genügt das.
 *
 * Fehlt der Storage ganz (private Modi), lebt die Session nur im Speicher der
 * laufenden Seite. Ein akzeptabler Rückfall, kein Fehlerfall.
 */
function tokenStore(): Storage | null {
  try {
    return import.meta.env.DEV ? sessionStorage : localStorage;
  } catch {
    return null;
  }
}

function loadStoredToken(): string | null {
  try {
    return tokenStore()?.getItem(TOKEN_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function storeToken(token: string): void {
  try {
    tokenStore()?.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // Ein voller oder gesperrter Storage darf die Verbindung nicht kosten.
  }
}

function newMsgId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Eine abgeschickte, aber noch nicht bestätigte Aktion.
 *
 * `phase` und `ballotNumber` halten fest, worauf sich die Aktion bezog. Nach
 * einem längeren Aussetzer wäre eine Stimme für einen abgeschlossenen Wahlgang
 * kein Nachreichen mehr, sondern eine Fehlermeldung — solche Einträge werden
 * verworfen statt gesendet.
 */
interface OutboxEntry {
  msgId: string;
  message: ReliableClientMessage;
  enqueuedAt: number;
  phase: string | null;
  ballotNumber: number | null;
}

type RoomEventListener = (event: RoomEvent) => void;
type RoomClosedListener = (reason: RoomClosedReason) => void;

/**
 * Verwaltet genau eine WebSocket-Verbindung zum Spielserver.
 *
 * Reconnect ist die zentrale Anforderung (GDD §29): Smartphones trennen die
 * Verbindung beim Sperren des Displays oft ohne sauberes Close-Event, deshalb
 * wird zusätzlich zum Backoff bei `online` und `visibilitychange` sofort ein
 * neuer Versuch ausgelöst.
 *
 * Drei Eigenschaften machen den Wiedereinstieg belastbar:
 *
 * 1. **Nichts geht still verloren** — Aktionen warten in der Outbox, bis der
 *    Server sie bestätigt hat, und werden nach dem Reconnect nachgereicht.
 * 2. **Eine tote Leitung wird als tot erkannt** — auf jeden Ping muss ein Pong
 *    folgen, sonst wird die Verbindung verworfen statt weiter beschrieben.
 * 3. **Aussichtslose Versuche hören auf** — bei veraltetem Protokoll oder einem
 *    zweiten Tab wird nicht endlos weiterprobiert.
 */
export class WsClient {
  private socket: WebSocket | null = null;
  private reconnectDelay = RECONNECT_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private everConnected = false;
  private bestRttMs = Infinity;
  private lastPongAt = 0;
  /** Gesetzt, wenn weitere Versuche nichts bringen würden. */
  private halted = false;
  private outbox: OutboxEntry[] = [];
  /** Nach `hello:ok` wird auf den ersten Zustand gewartet, bevor nachgereicht wird. */
  private awaitingStateForFlush = false;
  private readonly roomEventListeners = new Set<RoomEventListener>();
  private readonly roomClosedListeners = new Set<RoomClosedListener>();

  start(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', () => this.reconnectNow());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.reconnectNow();
    });
    this.connect();
  }

  onRoomEvent(listener: RoomEventListener): () => void {
    this.roomEventListeners.add(listener);
    return () => this.roomEventListeners.delete(listener);
  }

  onRoomClosed(listener: RoomClosedListener): () => void {
    this.roomClosedListeners.add(listener);
    return () => this.roomClosedListeners.delete(listener);
  }

  /**
   * Reiht eine Aktion ein und schickt sie, sobald es geht.
   *
   * Der Rückgabewert ist bewusst keiner: Ob die Aktion angekommen ist, zeigt
   * der Zustand vom Server — nicht ein Versprechen des Clients.
   */
  send(message: ReliableClientMessage): void {
    const view = useAppStore.getState().view;
    const entry: OutboxEntry = {
      msgId: newMsgId(),
      message,
      enqueuedAt: Date.now(),
      phase: view?.phase ?? null,
      ballotNumber: view?.ballot?.ballotNumber ?? null,
    };

    this.outbox.push(entry);
    if (this.outbox.length > OUTBOX_LIMIT) this.outbox.shift();
    this.publishPendingCount();
    this.transmit(entry);
  }

  /** Holt eine angehaltene Verbindung zurück (anderer Tab, manueller Versuch). */
  resume(): void {
    this.halted = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = RECONNECT_MIN_MS;
    this.reconnectNow();
  }

  private transmit(entry: OutboxEntry): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({ ...entry.message, msgId: entry.msgId }));
  }

  /** Handschlag und Herzschlag laufen an der Outbox vorbei — sie gehören der Verbindung. */
  private sendRaw(message: Extract<ClientMessage, { type: 'hello' } | { type: 'ping' }>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private reconnectNow(): void {
    if (this.halted) return;
    const socket = this.socket;
    if (socket?.readyState === WebSocket.OPEN) return;
    // Ein Socket, der im Aufbau feststeckt, wird nicht respektiert, sondern
    // ersetzt — sonst blockiert ein hängender Versuch den echten Reconnect.
    if (socket?.readyState === WebSocket.CONNECTING && this.connectTimer !== null) return;

    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectDelay = RECONNECT_MIN_MS;
    this.connect();
  }

  private connect(): void {
    if (this.halted) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.discardSocket();

    const store = useAppStore.getState();
    store.setConnectionStatus(this.everConnected ? 'reconnecting' : 'connecting');
    store.setNextRetryAt(null);

    const socket = new WebSocket(wsUrl());
    this.socket = socket;

    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      if (this.socket === socket && socket.readyState === WebSocket.CONNECTING) socket.close();
    }, CONNECT_TIMEOUT_MS);

    socket.addEventListener('open', () => {
      this.clearConnectTimer();
      this.sendRaw({
        type: 'hello',
        protocolVersion: PROTOCOL_VERSION,
        sessionToken: loadStoredToken(),
      });
    });

    socket.addEventListener('message', (event) => {
      this.handleMessage(String(event.data));
    });

    socket.addEventListener('close', () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearConnectTimer();
      this.stopPing();
      if (this.halted) return;
      useAppStore.getState().setConnectionStatus(this.everConnected ? 'reconnecting' : 'offline');
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => socket.close());
  }

  private discardSocket(): void {
    const socket = this.socket;
    if (!socket) return;
    this.socket = null;
    this.clearConnectTimer();
    if (socket.readyState <= WebSocket.OPEN) socket.close();
  }

  private clearConnectTimer(): void {
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.halted || this.reconnectTimer) return;
    this.reconnectAttempts += 1;
    const cap =
      this.reconnectAttempts > RECONNECT_PATIENT_AFTER ? RECONNECT_PATIENT_MAX_MS : RECONNECT_MAX_MS;
    const jitter = Math.random() * 250;
    const delay = Math.min(this.reconnectDelay, cap) + jitter;

    useAppStore.getState().setNextRetryAt(Date.now() + delay);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, cap);
  }

  /** Bricht alle weiteren Versuche ab; nur `resume()` hebt das wieder auf. */
  private halt(status: 'outdated' | 'takenOver'): void {
    this.halted = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopPing();
    this.discardSocket();
    const store = useAppStore.getState();
    store.setConnectionStatus(status);
    store.setNextRetryAt(null);
  }

  private handleMessage(raw: string): void {
    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }

    const result = ServerMessageSchema.safeParse(payload);
    if (!result.success) return;
    this.dispatch(result.data);
  }

  private dispatch(message: ServerMessage): void {
    const store = useAppStore.getState();

    switch (message.type) {
      case 'hello:ok': {
        this.everConnected = true;
        this.reconnectDelay = RECONNECT_MIN_MS;
        this.reconnectAttempts = 0;
        storeToken(message.sessionToken);
        store.setSession({ token: message.sessionToken, playerId: message.playerId });
        store.setConnectionStatus('connected');
        store.setNextRetryAt(null);
        store.setClockOffset(message.serverNow - Date.now());
        this.bestRttMs = Infinity;
        this.lastPongAt = Date.now();
        this.startPing();

        // Der Server sagt verbindlich, in welchem Raum diese Session steckt.
        // Ohne diesen Abgleich konnte der Client mit abgelaufenem Token auf
        // einem Spielbildschirm hängen bleiben, den es nicht mehr gibt.
        const current = store.view;
        if (message.roomId === null || (current && current.roomId !== message.roomId)) {
          store.clearView();
        }
        this.awaitingStateForFlush = message.roomId !== null;
        if (!this.awaitingStateForFlush) this.flushOutbox();
        return;
      }
      case 'ack':
        this.resolveEntry(message.msgId);
        return;
      case 'session:takeover':
        this.halt('takenOver');
        return;
      case 'pong': {
        this.lastPongAt = Date.now();
        const rtt = Date.now() - message.sentAt;
        // Nur die bislang schnellste Antwort einer Session bestimmt den Offset —
        // ein einzelner langsamer Roundtrip soll den Timer nicht verfälschen.
        if (rtt <= this.bestRttMs) {
          this.bestRttMs = rtt;
          store.setClockOffset(message.serverNow + rtt / 2 - Date.now());
        }
        return;
      }
      case 'lobby:list':
        store.setLobbyRooms(message.rooms);
        return;
      case 'room:state':
        store.setView(message.view);
        if (this.awaitingStateForFlush) {
          this.awaitingStateForFlush = false;
          this.flushOutbox();
        }
        return;
      case 'room:closed':
        store.clearView();
        store.setRejoin(null);
        for (const listener of this.roomClosedListeners) listener(message.reason);
        return;
      case 'room:event':
        for (const listener of this.roomEventListeners) listener(message.event);
        return;
      case 'rejoin:options':
        store.setRejoinOptions(message.options);
        return;
      case 'rejoin:pending':
        store.setRejoin(
          message.status === 'PENDING'
            ? { requestId: message.requestId, status: 'PENDING' }
            : { requestId: message.requestId, status: message.status },
        );
        return;
      case 'rejoin:requests':
        store.setRejoinRequests(message.requests);
        return;
      case 'error':
        // Eine abgelehnte Aktion wird nicht wiederholt — der Server hat
        // entschieden, und ein zweiter Anlauf endete genauso.
        if (message.msgId) this.resolveEntry(message.msgId);
        store.pushError(message.code);
        if (message.code === 'PROTOCOL_MISMATCH') this.handleOutdated();
        return;
    }
  }

  /**
   * Das Protokoll passt nicht mehr — weitere Versuche mit diesem Code sind
   * zwecklos. Statt endlos zu reconnecten wird neu geladen.
   */
  private handleOutdated(): void {
    this.halt('outdated');
    if (typeof window === 'undefined') return;
    setTimeout(() => location.reload(), RELOAD_DELAY_MS);
  }

  private resolveEntry(msgId: string): void {
    const before = this.outbox.length;
    this.outbox = this.outbox.filter((entry) => entry.msgId !== msgId);
    if (this.outbox.length !== before) this.publishPendingCount();
  }

  /**
   * Reicht nach — aber nur, was noch in die Gegenwart passt.
   *
   * Der Abgleich läuft gegen den frisch empfangenen Zustand, deshalb wird
   * bewusst erst nach dem ersten `room:state` geleert und nicht schon bei
   * `hello:ok`.
   */
  private flushOutbox(): void {
    const view = useAppStore.getState().view;
    const now = Date.now();

    const survivors = this.outbox.filter((entry) => {
      if (now - entry.enqueuedAt > OUTBOX_MAX_AGE_MS) return false;
      if (entry.phase === null) return true;
      if (!view || view.phase !== entry.phase) return false;
      return entry.ballotNumber === (view.ballot?.ballotNumber ?? null);
    });

    const changed = survivors.length !== this.outbox.length;
    this.outbox = survivors;
    if (changed) this.publishPendingCount();
    for (const entry of survivors) this.transmit(entry);
  }

  private publishPendingCount(): void {
    useAppStore.getState().setPendingActions(this.outbox.length);
  }

  private startPing(): void {
    this.stopPing();
    this.sendPing();
    this.pingTimer = setInterval(() => this.sendPing(), PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  /**
   * Jeder Ping prüft zugleich, ob der vorige beantwortet wurde.
   *
   * Ein Socket kann offen wirken, obwohl nichts mehr durchkommt — beim Sperren
   * des Displays ist das der Normalfall. Ohne diese Prüfung meldet der Client
   * bis zum serverseitigen Herzschlag-Timeout „verbunden".
   */
  private sendPing(): void {
    if (this.lastPongAt > 0 && Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
      this.stopPing();
      this.discardSocket();
      useAppStore.getState().setConnectionStatus('reconnecting');
      this.scheduleReconnect();
      return;
    }
    this.sendRaw({ type: 'ping', sentAt: Date.now() });
  }
}

export const wsClient = new WsClient();
